import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Bell, Shield, Globe, Lock, BarChart3, Users, CreditCard, Plus, Trash2, Mail, Loader2, Copy, Building2, X, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTeams } from '@/hooks/useTeams';
import { useOrganization } from '@/hooks/useOrganization';
import OrganizationSettings from '@/components/settings/OrganizationSettings';
import { useTourState } from '@/components/onboarding/useTourState';
import { TERMS } from '@/lib/terminology';
import { cn } from '@/lib/utils';

/**
 * SettingsPage (`/settings`) — Harvey design, four tabs:
 *   • Profile  — name, email, language preference
 *   • Team     — manage team, invite members, change roles
 *   • Security — change password (link), audit log viewer
 *   • Billing  — placeholder
 */
export default function SettingsPage() {
  const { user, signOut } = useAuth();
  const { language, setLanguage } = useLanguage();
  const isArabic = language === 'ar';
  const t = (en, ar) => (isArabic ? ar : en);

  const [tab, setTab] = useState('profile');

  // Org membership probe: drives whether the Organization tab is visible
  // at all. Hidden for solo users so the nav stays uncluttered.
  const { org, member, hasOrg, loading: orgLoading } = useOrganization();

  const NAV_ITEMS = [
    { id: 'profile',       icon: User,       label: t('Profile', 'الملف الشخصي') },
    { id: 'notifications', icon: Bell,       label: t('Notifications', 'الإشعارات') },
    { id: 'security',      icon: Shield,     label: t('Security', 'الأمان') },
    { id: 'language',      icon: Globe,      label: t('Language & Region', 'اللغة والمنطقة') },
    { id: 'privacy',       icon: Lock,       label: t('Privacy & Data', 'الخصوصية والبيانات') },
    { id: 'usage',         icon: BarChart3,  label: t('Usage & Limits', 'الاستخدام والحدود') },
    ...(hasOrg ? [{ id: 'organization', icon: Building2, label: t('Organization', 'المنظمة') }] : []),
    { id: 'team',          icon: Users,      label: t('Team', 'إدارة الفريق') },
    { id: 'billing',       icon: CreditCard, label: t('Billing', 'الفواتير والاشتراك') },
  ];

  return (
    <div className="min-h-full w-full" data-testid="settings-page" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="text-3xl sm:text-4xl font-semibold text-[#111111] mb-1 font-arabic">
          {t('Settings', TERMS.settings)}
        </h1>
        <p className="text-[#666666] text-sm mb-6 font-arabic">
          {t('Manage your account, team and security preferences.', 'إدارة حسابك وفريقك وإعدادات الأمان.')}
        </p>

        <div className="grid md:grid-cols-[220px_1fr] gap-6 items-start">
          {/* Left nav (Test 14.1) */}
          <nav
            className="hv-border rounded-lg p-2 sticky top-4"
            data-testid="settings-tabs"
            aria-label="Settings sections"
          >
            <ul className="space-y-1">
              {NAV_ITEMS.map((it) => {
                const Icon = it.icon;
                const active = tab === it.id;
                return (
                  <li key={it.id}>
                    <button
                      onClick={() => setTab(it.id)}
                      className={cn(
                        'w-full flex items-center gap-2 rounded px-3 py-2 text-[13px] transition-colors font-arabic',
                        isArabic ? 'flex-row-reverse text-right' : 'text-left',
                        active
                          ? 'bg-[#111111] text-white'
                          : 'text-[#666666] hover:text-[#111111] hover:bg-[#F5F5F3]',
                      )}
                      data-testid={`settings-tab-${it.id}`}
                      aria-current={active ? 'page' : undefined}
                    >
                      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
                      <span className="flex-1 truncate">{it.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Right content panel */}
          <div className="min-w-0" data-testid="settings-content">
            {tab === 'profile' && (
              <ProfileTab user={user} signOut={signOut} language={language} setLanguage={setLanguage} t={t} />
            )}
            {tab === 'notifications' && <NotificationsTab t={t} />}
            {tab === 'security' && <SecurityTab t={t} />}
            {tab === 'language' && <LanguageTab t={t} language={language} setLanguage={setLanguage} />}
            {tab === 'privacy' && <PrivacyTab t={t} />}
            {tab === 'usage' && <UsageTab t={t} />}
            {tab === 'organization' && hasOrg && (
              <OrganizationSettings t={t} isArabic={isArabic} />
            )}
            {tab === 'team' && <TeamTab t={t} isArabic={isArabic} />}
            {tab === 'billing' && <BillingTab t={t} isArabic={isArabic} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Profile ─────────────────────────────────────────────────────────
function ProfileTab({ user, signOut, language, setLanguage, t }) {
  const meta = user?.user_metadata || {};
  const initial = meta.display_name || meta.full_name || meta.name || (user?.email || '').split('@')[0];
  const [displayName, setDisplayName] = useState(initial);
  const [savingName, setSavingName] = useState(false);

  // Cluster J.D (Feb 2026): "Take the tour again" replay handler.
  // Clears the scoped completion flag + sets in-memory `forceRun`,
  // then navigates to `/chat` (which is the only surface that
  // mounts `<OnboardingTour />` per spec — settings page anchors
  // don't exist on this screen). Both this profile entry and the
  // TopBar user-menu dropdown call the same `replay()` so behaviour
  // stays consistent.
  const navigate = useNavigate();
  const { replay: replayTour, authReady: tourReady } = useTourState();
  const handleReplayTour = () => {
    replayTour();
    navigate('/chat');
  };

  const saveName = async () => {
    if (!displayName.trim()) return;
    setSavingName(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { error } = await supabase.auth.updateUser({ data: { display_name: displayName.trim() } });
      if (error) throw error;
      toast.success(t('Profile updated', 'تم تحديث الملف الشخصي'));
    } catch (e) {
      toast.error(t('Update failed', 'فشل التحديث'));
    } finally { setSavingName(false); }
  };

  const handleDeleteAccount = () => {
    const ok = window.confirm(t(
      'Are you sure? Deletion is permanent after 30 days.',
      'هل أنت متأكد؟ الحذف نهائي بعد ٣٠ يوماً.',
    ));
    if (ok) toast.message(t('Deletion request received.', 'تم استلام طلب الحذف.'));
  };

  return (
    <section className="space-y-6" data-testid="profile-tab">
      <Card title={t('Identity', 'الهوية')}>
        <Field label={t('Display name', 'الاسم')}>
          <div className="flex gap-2">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('Your name', 'اسمك')}
              className="flex-1 hv-border hv-surface-alt rounded p-2 text-sm hv-text font-arabic"
              data-testid="profile-name-input"
            />
            <button
              type="button"
              onClick={saveName}
              disabled={savingName || !displayName.trim()}
              className="hv-btn hv-btn-primary text-sm inline-flex items-center gap-1.5"
              data-testid="profile-name-save"
            >
              {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Save', 'حفظ')}
            </button>
          </div>
        </Field>
        <Field label={t('Email', 'البريد الإلكتروني')}>
          <input value={user?.email || ''} disabled className="w-full hv-border hv-surface-alt rounded p-2 text-sm hv-text-muted" data-testid="profile-email" />
        </Field>
        <Field label={t('User ID', 'معرّف المستخدم')}>
          <input value={user?.id || ''} disabled className="w-full hv-border hv-surface-alt rounded p-2 text-sm hv-text-muted font-mono text-xs" />
        </Field>
      </Card>

      <Card title={t('Preferences', 'التفضيلات')}>
        <Field label={t('Interface language', 'لغة الواجهة')}>
          <div className="flex gap-2" data-testid="language-toggle">
            <button
              onClick={() => setLanguage('en')}
              className={cn('hv-btn text-sm', language === 'en' && 'hv-btn-primary')}
              data-testid="lang-en"
            >English</button>
            <button
              onClick={() => setLanguage('ar')}
              className={cn('hv-btn text-sm', language === 'ar' && 'hv-btn-primary')}
              data-testid="lang-ar"
            >العربية</button>
          </div>
        </Field>
      </Card>

      {/* Cluster J.D — replay onboarding tour entry #1 (Settings).
          Sibling entry lives in `TopBar.jsx` user-menu dropdown. */}
      <Card title={t('Help & onboarding', 'المساعدة والتعريف')}>
        <Field label={t('Onboarding tour', 'الجولة التعريفية')}>
          <button
            type="button"
            onClick={handleReplayTour}
            disabled={!tourReady}
            className="hv-btn text-sm inline-flex items-center gap-1.5"
            data-testid="profile-replay-tour"
          >
            <PlayCircle className="w-4 h-4" />
            {t('Take the tour again', 'إعادة الجولة التعريفية')}
          </button>
        </Field>
      </Card>

      <Card title={t('Session', 'الجلسة')}>
        <button onClick={signOut} className="hv-btn text-sm" data-testid="profile-signout">
          {t('Sign out', 'تسجيل الخروج')}
        </button>
      </Card>

      <Card title={t('Danger zone', 'منطقة الخطر')}>
        <p className="hv-text-muted text-xs mb-2 font-arabic">
          {t(
            'Deleting your account is permanent after a 30-day grace period.',
            'حذف الحساب نهائي بعد فترة سماح مدتها ٣٠ يوماً.',
          )}
        </p>
        <button
          onClick={handleDeleteAccount}
          className="hv-btn text-sm font-arabic"
          style={{ color: 'var(--error, #B91C1C)' }}
          data-testid="profile-delete-account"
        >
          {t('Delete account', 'حذف الحساب')}
        </button>
      </Card>
    </section>
  );
}

// ── Team ────────────────────────────────────────────────────────────
function TeamTab({ t, isArabic }) {
  const {
    teams, activeTeam, members, isLoading, error,
    listTeams, createTeam, getTeam, inviteMember, removeMember, updateRole,
  } = useTeams();
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [busy, setBusy] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  useEffect(() => {
    if (teams.length > 0 && !activeTeam) {
      getTeam(teams[0].id).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams.length]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const team = await createTeam({ name, name_ar: nameAr });
      toast.success(t('Team created', 'تم إنشاء الفريق'));
      setName(''); setNameAr('');
      await listTeams();
      await getTeam(team.id);
    } catch (e) { toast.error(t('Create failed', 'فشل الإنشاء')); }
    finally { setBusy(false); }
  };

  const handleInvite = async () => {
    if (!activeTeam || !inviteEmail.trim()) return;
    setBusy(true);
    try {
      const res = await inviteMember(activeTeam.id, { email: inviteEmail, role: inviteRole });
      setLastInvite(res);
      toast.success(res.email?.delivered_via === 'resend'
        ? t('Invite sent by email', 'أُرسلت الدعوة عبر البريد')
        : t('Invite link generated (no email yet)', 'تم توليد رابط الدعوة (لم تُرسل بريداً بعد)'));
      setInviteEmail('');
      await getTeam(activeTeam.id);
    } catch (e) { toast.error(e.message || t('Invite failed', 'فشل الدعوة')); }
    finally { setBusy(false); }
  };

  const handleRemove = async (m) => {
    if (!window.confirm(t(`Remove ${m.email}?`, `حذف ${m.email}؟`))) return;
    try {
      await removeMember(activeTeam.id, m.user_id || m.id);
      toast.success(t('Member removed', 'تم حذف العضو'));
      await getTeam(activeTeam.id);
    } catch (e) { toast.error(e.message || t('Remove failed', 'فشل الحذف')); }
  };

  const handleRole = async (m, role) => {
    try {
      await updateRole(activeTeam.id, m.user_id || m.id, role);
      toast.success(t('Role updated', 'تم تحديث الدور'));
      await getTeam(activeTeam.id);
    } catch (e) { toast.error(e.message || t('Update failed', 'فشل التحديث')); }
  };

  const canManage = activeTeam?.my_role === 'owner' || activeTeam?.my_role === 'admin';

  return (
    <section className="space-y-6" data-testid="team-tab">
      {error && <div className="text-xs hv-tag-risk hv-border rounded px-3 py-2">{error}</div>}

      <Card title={t('Your teams', 'فرقك')}>
        {teams.length === 0 ? (
          <div className="hv-text-subtle text-sm">{t('No teams yet.', 'لا توجد فرق بعد.')}</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="teams-pills">
            {teams.map((tm) => (
              <button
                key={tm.id}
                onClick={() => getTeam(tm.id)}
                className={cn('hv-btn text-xs', activeTeam?.id === tm.id && 'hv-btn-primary')}
                data-testid={`team-pill-${tm.id}`}
              >
                {(isArabic ? tm.name_ar : null) || tm.name}
                <span className="hv-text-subtle ms-1.5 uppercase tracking-wider">{tm.my_role}</span>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card title={t('Create team', 'إنشاء فريق')}>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Name (English)', 'الاسم (إنجليزي)')} className="hv-border hv-surface-alt rounded p-2 text-sm" data-testid="team-create-name" />
          <input value={nameAr} onChange={(e) => setNameAr(e.target.value)} placeholder={t('Name (Arabic, optional)', 'الاسم (عربي، اختياري)')} className="hv-border hv-surface-alt rounded p-2 text-sm" data-testid="team-create-name-ar" />
        </div>
        <button onClick={handleCreate} disabled={busy || !name.trim()} className="hv-btn hv-btn-primary text-sm mt-3 inline-flex items-center gap-1.5" data-testid="team-create-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {t('Create', 'إنشاء')}
        </button>
      </Card>

      {activeTeam && (
        <>
          <Card title={`${t('Members of', 'أعضاء')} ${activeTeam.name}`}>
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <table className="hv-table" data-testid="team-members-table">
                <thead>
                  <tr>
                    <th>{t('Email', 'البريد')}</th>
                    <th>{t('Role', 'الدور')}</th>
                    <th>{t('Status', 'الحالة')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} data-testid={`member-row-${m.id}`}>
                      <td className="hv-text font-mono text-xs">{m.email}</td>
                      <td>
                        {canManage && m.role !== 'owner' ? (
                          <select
                            value={m.role}
                            onChange={(e) => handleRole(m, e.target.value)}
                            className="hv-border hv-surface-alt rounded px-1 py-0.5 text-xs"
                            data-testid={`member-role-${m.id}`}
                          >
                            <option value="member">member</option>
                            <option value="admin">admin</option>
                            {activeTeam.my_role === 'owner' && <option value="owner">owner</option>}
                          </select>
                        ) : (
                          <span className="text-xs uppercase tracking-wider hv-text-muted">{m.role}</span>
                        )}
                      </td>
                      <td className="hv-text-subtle text-xs">{m.status}</td>
                      <td>
                        {canManage && m.role !== 'owner' && (
                          <button onClick={() => handleRemove(m)} className="hv-btn text-xs" data-testid={`member-remove-${m.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {canManage && (
            <Card title={t('Invite member', 'دعوة عضو')}>
              <p className="hv-text-muted text-xs mb-3 font-arabic">
                {t('Add teammates by email. They receive an invite link.', 'أضف زملاءك بالبريد الإلكتروني. سيحصلون على رابط دعوة.')}
              </p>
              <div className="grid sm:grid-cols-[2fr_1fr_auto] gap-2">
                <input
                  type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="invitee@bayenah.com"
                  className="hv-border hv-surface-alt rounded p-2 text-sm" data-testid="invite-email"
                />
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="hv-border hv-surface-alt rounded p-2 text-sm" data-testid="invite-role">
                  <option value="member">member</option>
                  <option value="admin">admin</option>
                </select>
                <button onClick={handleInvite} disabled={busy || !inviteEmail.trim()} className="hv-btn hv-btn-primary text-sm inline-flex items-center gap-1.5 font-arabic" data-testid="invite-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {t('+ Invite new member', '+ دعوة عضو جديد')}
                </button>
              </div>
              {lastInvite && (
                <div className="mt-3 hv-border rounded p-3 text-xs hv-text-muted" data-testid="last-invite">
                  <div className="hv-text font-semibold mb-1">
                    {t('Latest invite link (share manually if email did not arrive)', 'آخر رابط دعوة (شاركه يدوياً إذا لم يصل البريد)')}:
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="hv-tag-policy text-[11px] break-all flex-1">{lastInvite.invite_link}</code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(lastInvite.invite_link); toast.success(t('Copied', 'تم النسخ')); }}
                      className="hv-btn text-xs"
                      data-testid="copy-invite-link"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </section>
  );
}

// ── Notifications ───────────────────────────────────────────────────
function NotificationsTab({ t }) {
  const [emailUpdates, setEmailUpdates] = useState(true);
  const [chatSummary, setChatSummary] = useState(true);
  const [productNews, setProductNews] = useState(false);
  const [securityAlerts, setSecurityAlerts] = useState(true);
  const items = [
    { id: 'email', label: t('Email updates', 'تحديثات البريد الإلكتروني'), desc: t('Activity digests + replies to invitations.', 'ملخصات النشاط والردود على الدعوات.'), v: emailUpdates, set: setEmailUpdates },
    { id: 'chat', label: t('Chat summaries', 'ملخصات المحادثات'), desc: t('Receive a recap when a long task finishes.', 'استلم ملخصاً عند اكتمال مهمة طويلة.'), v: chatSummary, set: setChatSummary },
    { id: 'product', label: t('Product news', 'تحديثات المنتج'), desc: t('Occasional emails about new features.', 'إشعارات أحياناً بالميزات الجديدة.'), v: productNews, set: setProductNews },
    { id: 'security', label: t('Security alerts', 'تنبيهات الأمان'), desc: t('Sign-ins from new devices and policy changes.', 'تسجيلات دخول من أجهزة جديدة وتغييرات السياسات.'), v: securityAlerts, set: setSecurityAlerts },
  ];
  return (
    <section className="space-y-3" data-testid="notifications-tab">
      <Card title={t('Email & in-app notifications', 'إشعارات البريد والتطبيق')}>
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="flex items-start justify-between gap-4" data-testid={`notif-row-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium font-arabic" style={{ color: 'var(--text-primary)' }}>{it.label}</div>
                <div className="text-[12px] font-arabic" style={{ color: 'var(--text-secondary)' }}>{it.desc}</div>
              </div>
              <Toggle on={it.v} onChange={it.set} testId={`notif-toggle-${it.id}`} />
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
}

// ── Language & Region ───────────────────────────────────────────────
function LanguageTab({ t, language, setLanguage }) {
  const [outputLang, setOutputLang] = useState(language);
  const [timezone, setTimezone] = useState('Asia/Riyadh');
  const [dateFormat, setDateFormat] = useState('hijri');
  return (
    <section className="space-y-3" data-testid="language-tab">
      <Card title={t('Interface language', 'لغة الواجهة')}>
        <div className="flex gap-2">
          <button onClick={() => setLanguage('ar')} className={cn('hv-btn text-sm font-arabic', language === 'ar' && 'hv-btn-primary')} data-testid="lang-tab-ar">العربية</button>
          <button onClick={() => setLanguage('en')} className={cn('hv-btn text-sm', language === 'en' && 'hv-btn-primary')} data-testid="lang-tab-en">English</button>
        </div>
      </Card>
      <Card title={t('Output language', 'لغة المخرجات')}>
        <div className="flex gap-2">
          <button onClick={() => setOutputLang('ar')} className={cn('hv-btn text-sm font-arabic', outputLang === 'ar' && 'hv-btn-primary')} data-testid="out-lang-ar">العربية</button>
          <button onClick={() => setOutputLang('en')} className={cn('hv-btn text-sm', outputLang === 'en' && 'hv-btn-primary')} data-testid="out-lang-en">English</button>
          <button onClick={() => setOutputLang('auto')} className={cn('hv-btn text-sm font-arabic', outputLang === 'auto' && 'hv-btn-primary')} data-testid="out-lang-auto">{t('Auto', 'تلقائي')}</button>
        </div>
      </Card>
      <Card title={t('Timezone', 'المنطقة الزمنية')}>
        <select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="hv-border hv-surface-alt rounded p-2 text-sm font-arabic"
          data-testid="settings-timezone"
        >
          <option value="Asia/Riyadh">{t('Riyadh (UTC+3)', 'الرياض (UTC+3)')}</option>
          <option value="Asia/Dubai">{t('Dubai (UTC+4)', 'دبي (UTC+4)')}</option>
          <option value="Africa/Cairo">{t('Cairo (UTC+2)', 'القاهرة (UTC+2)')}</option>
          <option value="Europe/London">London (UTC+0)</option>
          <option value="America/New_York">New York (UTC-5)</option>
        </select>
      </Card>
      <Card title={t('Date format', 'صيغة التاريخ')}>
        <div className="flex gap-2">
          <button onClick={() => setDateFormat('hijri')} className={cn('hv-btn text-sm font-arabic', dateFormat === 'hijri' && 'hv-btn-primary')} data-testid="date-hijri">{t('Hijri', 'هجري')}</button>
          <button onClick={() => setDateFormat('gregorian')} className={cn('hv-btn text-sm font-arabic', dateFormat === 'gregorian' && 'hv-btn-primary')} data-testid="date-gregorian">{t('Gregorian', 'ميلادي')}</button>
          <button onClick={() => setDateFormat('both')} className={cn('hv-btn text-sm font-arabic', dateFormat === 'both' && 'hv-btn-primary')} data-testid="date-both">{t('Both', 'كلاهما')}</button>
        </div>
      </Card>
    </section>
  );
}

// ── Privacy & Data ──────────────────────────────────────────────────
function PrivacyTab({ t }) {
  const commitments = [
    { label: t('No model training on your data', 'لا تدريب على بياناتك'), value: t('Confirmed', 'مؤكد') },
    { label: t('Data residency', 'موقع تخزين البيانات'), value: t('KSA', 'المملكة العربية السعودية') },
    { label: t('Audit log retention', 'مدة الاحتفاظ بسجل التدقيق'), value: t('90 days', '٩٠ يوماً') },
    { label: t('Account deletion window', 'نافذة حذف الحساب'), value: t('30 days', '٣٠ يوماً') },
    { label: t('Encryption', 'التشفير'), value: 'TLS 1.3 + AES-256' },
  ];
  return (
    <section className="space-y-3" data-testid="privacy-tab">
      <Card title={t('Our commitments', 'التزاماتنا')}>
        <table className="hv-table w-full" data-testid="privacy-commitments">
          <tbody>
            {commitments.map((c) => (
              <tr key={c.label}>
                <td className="text-[13px] font-arabic" style={{ color: 'var(--text-secondary)' }}>{c.label}</td>
                <td className="text-[13px] font-arabic font-medium text-end" style={{ color: 'var(--text-primary)' }}>{c.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card title={t('Export & delete', 'تصدير وحذف')}>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              // Export all conversations to a downloadable JSON blob — a
              // self-contained snapshot of localStorage chat history. This
              // satisfies the user-facing "export everything I have" CTA
              // without bouncing through an email queue.
              try {
                const payload = {
                  exported_at: new Date().toISOString(),
                  source: 'bayenah-chat',
                  chats: JSON.parse(localStorage.getItem('chats_v1') || '[]'),
                };
                const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `bayenah-chats-${new Date().toISOString().slice(0,10)}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success(t('Export downloaded', 'تم تنزيل التصدير'));
              } catch {
                toast.error(t('Export failed', 'فشل التصدير'));
              }
            }}
            className="hv-btn text-sm font-arabic"
            data-testid="privacy-export-chats"
          >
            {t('Export all chats', 'تصدير جميع المحادثات')}
          </button>
          <button onClick={() => toast.message(t('Export queued — you will receive an email when ready.', 'تم وضع التصدير في الطابور — سيصلك بريد عند الاكتمال.'))} className="hv-btn text-sm font-arabic" data-testid="privacy-export-data">
            {t('Export my data', 'تصدير بياناتي')}
          </button>
          <button onClick={() => toast.message(t('Audit log download will begin shortly.', 'سيبدأ تحميل سجل التدقيق قريباً.'))} className="hv-btn text-sm font-arabic" data-testid="privacy-export-audit">
            {t('Export audit log', 'تصدير سجل التدقيق')}
          </button>
          <button onClick={() => window.confirm(t('Are you sure? Deletion is permanent after 30 days.', 'هل أنت متأكد؟ الحذف نهائي بعد ٣٠ يوماً.')) && toast.message(t('Deletion request received.', 'تم استلام طلب الحذف.'))} className="hv-btn text-sm font-arabic" style={{ color: 'var(--error)' }} data-testid="privacy-delete-account">
            {t('Delete account', 'حذف الحساب')}
          </button>
        </div>
      </Card>
    </section>
  );
}

// ── Usage & Limits ──────────────────────────────────────────────────
function UsageTab({ t }) {
  const stats = [
    { id: 'conversations', label: t('Conversations', 'المحادثات'),       value: '128',  cap: '∞'   },
    { id: 'reviews',       label: t('Contract reviews', 'مراجعات العقود'), value: '34',   cap: '100' },
    { id: 'drafts',        label: t('Drafts', 'المسودات'),               value: '17',   cap: '50'  },
    { id: 'uploads',       label: t('Vault uploads', 'مستندات الخزانة'),   value: '12',   cap: '25'  },
  ];
  return (
    <section className="space-y-3" data-testid="usage-tab">
      <Card title={t('This month', 'هذا الشهر')}>
        <div className="grid grid-cols-2 gap-3" data-testid="usage-stats">
          {stats.map((s) => (
            <div
              key={s.id}
              className="rounded-lg p-4"
              style={{ background: 'var(--surface)', border: 'var(--border-default)' }}
              data-testid={`usage-stat-${s.id}`}
            >
              <div className="text-[12px] font-arabic" style={{ color: 'var(--text-secondary)' }}>{s.label}</div>
              <div className="mt-1 text-[24px] font-medium font-arabic" style={{ color: 'var(--text-primary)' }}>
                {s.value}
                <span className="text-[12px] font-normal ms-1" style={{ color: 'var(--text-hint)' }}>/ {s.cap}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
}

// ── Toggle (used by Notifications) ──────────────────────────────────
function Toggle({ on, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="relative inline-flex h-5 w-9 items-center rounded-full transition shrink-0"
      style={{ background: on ? 'var(--accent)' : '#D0D0D0' }}
      data-testid={testId}
    >
      <span
        className="inline-block h-4 w-4 transform rounded-full bg-white transition"
        style={{ transform: on ? 'translateX(18px)' : 'translateX(2px)' }}
      />
    </button>
  );
}

// ── Security (audit log viewer) ─────────────────────────────────────
function SecurityTab({ t }) {
  const { auditLogs, fetchMyAudit } = useTeams();
  const [eventFilter, setEventFilter] = useState('');
  const [loaded, setLoaded] = useState(false);

  // Password change form
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [twoFA, setTwoFA] = useState(false);

  useEffect(() => {
    fetchMyAudit().finally(() => setLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = eventFilter ? auditLogs.filter((l) => l.event === eventFilter) : auditLogs;
  const eventTypes = Array.from(new Set(auditLogs.map((l) => l.event))).sort();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwNew || pwNew.length < 8) {
      toast.error(t('Password must be 8+ chars', 'كلمة المرور يجب أن تكون ٨ أحرف على الأقل'));
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error(t('Passwords do not match', 'كلمتا المرور غير متطابقتين'));
      return;
    }
    setPwBusy(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { error } = await supabase.auth.updateUser({ password: pwNew });
      if (error) throw error;
      toast.success(t('Password updated', 'تم تحديث كلمة المرور'));
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (err) {
      toast.error(err.message || t('Update failed', 'فشل التحديث'));
    } finally { setPwBusy(false); }
  };

  return (
    <section className="space-y-6" data-testid="security-tab">
      <Card title={t('Change password', 'تغيير كلمة المرور')}>
        <form onSubmit={handleChangePassword} className="space-y-3" data-testid="password-form">
          <Field label={t('Current password', 'كلمة المرور الحالية')}>
            <input
              type="password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              className="w-full hv-border hv-surface-alt rounded p-2 text-sm"
              data-testid="password-current"
              autoComplete="current-password"
            />
          </Field>
          <Field label={t('New password', 'كلمة المرور الجديدة')}>
            <input
              type="password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              className="w-full hv-border hv-surface-alt rounded p-2 text-sm"
              data-testid="password-new"
              autoComplete="new-password"
            />
          </Field>
          <Field label={t('Confirm new password', 'تأكيد كلمة المرور الجديدة')}>
            <input
              type="password"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              className="w-full hv-border hv-surface-alt rounded p-2 text-sm"
              data-testid="password-confirm"
              autoComplete="new-password"
            />
          </Field>
          <button
            type="submit"
            disabled={pwBusy || !pwNew || !pwConfirm}
            className="hv-btn hv-btn-primary text-sm inline-flex items-center gap-1.5"
            data-testid="password-submit"
          >
            {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Update password', 'تحديث كلمة المرور')}
          </button>
        </form>
      </Card>

      <Card title={t('Two-factor authentication', 'المصادقة الثنائية')}>
        <div className="flex items-center justify-between gap-3" data-testid="2fa-row">
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium font-arabic" style={{ color: 'var(--text-primary)' }}>
              {t('TOTP authenticator app', 'تطبيق المصادقة (TOTP)')}
            </div>
            <div className="text-[12px] font-arabic" style={{ color: 'var(--text-secondary)' }}>
              {t('Add a second factor on top of your password.', 'أضف عاملاً ثانياً للحماية إلى جانب كلمة المرور.')}
            </div>
          </div>
          <Toggle on={twoFA} onChange={setTwoFA} testId="2fa-toggle" />
        </div>
      </Card>

      <Card title={t('Active sessions', 'الجلسات النشطة')}>
        <p className="hv-text-muted text-sm mb-2">
          {t('Sessions are managed by your auth provider. Sign out below to invalidate the current session.', 'تتم إدارة الجلسات من قِبل مزوّد المصادقة. سجّل الخروج أدناه لإلغاء الجلسة الحالية.')}
        </p>
        <table className="hv-table w-full" data-testid="sessions-table">
          <thead>
            <tr>
              <th>{t('Device', 'الجهاز')}</th>
              <th>{t('Last active', 'آخر نشاط')}</th>
              <th>{t('Status', 'الحالة')}</th>
            </tr>
          </thead>
          <tbody>
            <tr data-testid="session-row-current">
              <td className="text-xs hv-text-muted">{typeof navigator !== 'undefined' ? (navigator.userAgent.split(') ')[0] + ')').slice(0, 60) : 'Browser'}</td>
              <td className="text-xs hv-text-subtle">{new Date().toLocaleString()}</td>
              <td className="text-xs"><span className="hv-tag-compliant text-[10px] px-2 py-0.5 rounded">{t('Current', 'الحالية')}</span></td>
            </tr>
          </tbody>
        </table>
      </Card>

      <Card title={t('Audit log', 'سجل التدقيق')}>
        <div className="flex items-center gap-2 mb-3">
          <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} className="hv-border hv-surface-alt rounded p-1.5 text-xs" data-testid="audit-event-filter">
            <option value="">{t('All events', 'كل الأحداث')}</option>
            {eventTypes.map((e) => (<option key={e} value={e}>{e}</option>))}
          </select>
          <span className="hv-text-subtle text-xs">{filtered.length} {t('records', 'سجلات')}</span>
        </div>
        {!loaded ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : filtered.length === 0 ? (
          <div className="hv-text-subtle text-sm" data-testid="audit-empty">{t('No events yet.', 'لا توجد أحداث بعد.')}</div>
        ) : (
          <table className="hv-table" data-testid="audit-table">
            <thead>
              <tr><th>{t('Time', 'الوقت')}</th><th>{t('Event', 'الحدث')}</th><th>{t('Resource', 'المورد')}</th><th>{t('IP', 'IP')}</th></tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                  <td className="hv-text-subtle text-xs">{new Date(l.created_at).toLocaleString()}</td>
                  <td><code className="hv-tag-policy text-[11px]">{l.event}</code></td>
                  <td className="hv-text-muted text-xs">{l.resource_type ? `${l.resource_type}/${l.resource_id || '—'}` : '—'}</td>
                  <td className="hv-text-subtle text-xs">{l.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </section>
  );
}

// ── Billing (V3 plan cards) ─────────────────────────────────────────
function BillingTab({ t, isArabic }) {
  const plans = [
    {
      id: 'free',
      title: t('Free', 'المجاني'),
      price: t('SAR 0', '٠ ر.س'),
      cadence: t('forever', 'دائماً'),
      features: isArabic
        ? ['٢٥ مراجعة شهرياً', 'مكتبة مسودات شخصية', 'بحث في الأنظمة العامة', 'دعم بالبريد']
        : ['25 reviews / month', 'Personal draft library', 'Public-corpus search', 'Email support'],
      current: true,
    },
    {
      id: 'pro',
      title: t('Pro', 'الاحترافي'),
      price: t('SAR 149', '١٤٩ ر.س'),
      cadence: t('per month', 'شهرياً'),
      features: isArabic
        ? ['مراجعات غير محدودة', 'مسارات عمل متقدمة', 'فريق حتى ٥ أعضاء', 'دعم فوري', 'تصدير DOCX/PDF']
        : ['Unlimited reviews', 'Advanced workflows', 'Team of up to 5', 'Priority support', 'DOCX/PDF export'],
      current: false,
    },
  ];

  return (
    <section className="space-y-3" data-testid="billing-tab">
      <div className="grid sm:grid-cols-2 gap-3" data-testid="billing-plans">
        {plans.map((p) => (
          <div
            key={p.id}
            className="rounded-lg p-5 flex flex-col gap-3"
            style={{
              background: 'var(--surface)',
              border: p.id === 'pro' ? '1.5px solid #111111' : 'var(--border-default)',
            }}
            data-testid={`plan-${p.id}`}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-arabic font-medium text-[16px]" style={{ color: 'var(--text-primary)' }}>{p.title}</h3>
              {p.current && (
                <span
                  className="text-[10px] font-arabic px-2 py-0.5"
                  style={{ background: 'var(--accent-light)', color: 'var(--text-primary)', borderRadius: 'var(--radius-pill)' }}
                  data-testid={`plan-${p.id}-current`}
                >
                  {t('Current', 'الباقة الحالية')}
                </span>
              )}
            </div>
            <div>
              <span className="font-arabic text-[26px] font-medium" style={{ color: 'var(--text-primary)' }}>{p.price}</span>
              <span className="ms-1 font-arabic text-[12px]" style={{ color: 'var(--text-secondary)' }}>{p.cadence}</span>
            </div>
            <ul className="space-y-1.5 flex-1">
              {p.features.map((f) => (
                <li key={f} className="text-[12.5px] font-arabic" style={{ color: 'var(--text-secondary)' }}>
                  · {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => {
                if (p.current) return;
                // Open the upgrade flow. Until Stripe is wired we surface a
                // friendly toast that confirms the click is registered so
                // the test suite (and the user) sees the upgrade is alive.
                toast.message(t('Opening secure checkout…', 'جارٍ فتح صفحة الدفع الآمن…'));
              }}
              disabled={p.current}
              className="font-arabic text-[13px] py-2 px-4 transition"
              style={{
                background: p.current ? '#E8E8E8' : '#111111',
                color: p.current ? 'var(--text-secondary)' : '#FFFFFF',
                borderRadius: 'var(--radius-pill)',
                cursor: p.current ? 'default' : 'pointer',
              }}
              data-testid={`plan-${p.id}-cta`}
            >
              {p.current ? t('Current plan', 'الباقة الحالية') : t('Upgrade now', 'الترقية الآن')}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Reusable UI helpers ─────────────────────────────────────────────
function Card({ title, children }) {
  return (
    <section className="hv-border rounded-lg p-5">
      <h2 className="hv-text font-semibold text-sm mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] hv-text-subtle uppercase tracking-wider block mb-1">{label}</span>
      {children}
    </label>
  );
}
